import React from 'react';
import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { IFinanceIncompleteReviewApproval, IEntityTerminationActionItem } from '../types';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import { APPROVE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW } from '../../../utils/statApi/EntityTerminationApi';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import FinanceIncompleteReviewApprovalForm from './FinanceIncompleteReviewApprovalForm';

interface IFinanceIncompleteReviewApprovalPanelProps {
  closePanel: () => void;
  data: IFinanceIncompleteReviewApproval;
  actionItems: IEntityTerminationActionItem[];
}

export const shouldShowMissingLegalTerminationTypeMessage = (
  review?: Pick<IFinanceIncompleteReviewApproval, 'legalTerminationType'>,
): boolean => !review?.legalTerminationType;

export const shouldShowUnmatchedLegalTerminationTypeMessage = (
  review?: Pick<
    IFinanceIncompleteReviewApproval,
    'legalTerminationType' | 'hasMatchingLifecycleStatus'
  >,
): boolean => !!review?.legalTerminationType && !review.hasMatchingLifecycleStatus;

const FinanceIncompleteReviewApprovalPanel: React.FC<
  IFinanceIncompleteReviewApprovalPanelProps
> = ({ closePanel, data, actionItems }) => {
  const form = useForm<IFinanceIncompleteReviewApproval>();
  const { handleSubmit, watch } = form;
  const lifecycleStatus = watch('lifecycleStatus');

  const [approveFinanceIncompleteReview, { loading, error }] = useMutation(
    APPROVE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW,
    {
      refetchQueries: ['FinanceIncompleteReview'],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IFinanceIncompleteReviewApproval): void => {
    approveFinanceIncompleteReview({
      variables: {
        financeIncompleteReviewInputDto: {
          reviewId: data.reviewId,
          approvalId: data.review.reviewApprovals?.[0]?.id,
          lifecycleStatusId: formData.lifecycleStatus?.id,
        },
      },
    });
  };

  const hasIncompleteActionItems = actionItems.some(
    (item) => !item.dateCompleted || (item.tasks ?? []).some((task) => !task.completedDate),
  );

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Approve Finance Incomplete Review"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Approve"
          saveTitle="Approve"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
          mutationLoading={loading}
          disabled={hasIncompleteActionItems || !lifecycleStatus?.fieldName?.trim()}
        />
      )}
    >
      <form>
        <LoadingErrorMessage error={error} loading={loading} />
        <FinanceIncompleteReviewApprovalForm
          form={form}
          actionItems={actionItems}
          showMissingLegalTerminationTypeMessage={shouldShowMissingLegalTerminationTypeMessage(
            data,
          )}
          showUnmatchedLegalTerminationTypeMessage={shouldShowUnmatchedLegalTerminationTypeMessage(
            data,
          )}
        />
      </form>
    </CoherencePanel>
  );
};

export default FinanceIncompleteReviewApprovalPanel;
